import {sortBy} from 'lodash';
import {useMemo} from 'react';
import {
  ContentSubmission,
  ContentSubmissionVersion,
  CONTENT_SUBMISSION_ANALYSIS_STATUS,
} from '@/types/contentSubmission';
import contentSubmissionFetchers from '@/api/fetchers/contentSubmission';
import useDataQuery from '@/hooks/use-data-query';

export const CONTENT_SUBMISSION_QUERY_KEY = 'contentSubmission';

const ANALYSIS_POLL_INTERVAL = 5000;

type ContentSubmissionQueryData = {data: ContentSubmission} | undefined;

function useContentSubmissionQuery({
  contentSubmissionId,
  enabled = true,
  shouldRefetchIfAnalysisIsProcessing = true,
}: {
  contentSubmissionId: string | undefined;
  enabled?: boolean;
  shouldRefetchIfAnalysisIsProcessing?: boolean;
}) {
  const response = useDataQuery<ContentSubmission>({
    queryKey: [CONTENT_SUBMISSION_QUERY_KEY, contentSubmissionId],
    queryFn: contentSubmissionFetchers.contentSubmission,
    enabled: !!contentSubmissionId && enabled,
    refetchInterval: (data: ContentSubmissionQueryData) => {
      if (!shouldRefetchIfAnalysisIsProcessing) return false;
      const isAnyVersionProcessing = data?.data?.versions?.some(
        (v: ContentSubmissionVersion) =>
          v.contentSubmissionAnalysisStatus ===
          CONTENT_SUBMISSION_ANALYSIS_STATUS.PROCESSING
      );
      return isAnyVersionProcessing ? ANALYSIS_POLL_INTERVAL : false;
    },
    meta: {
      contentSubmissionId,
    },
  });

  const sortedChildContentSubmissions = useMemo(
    () => sortBy(response.data?.childContentSubmissions, 'weight'),
    [response.data?.childContentSubmissions]
  );

  const data = useMemo(
    () =>
      response.data
        ? {
            ...response.data,
            childContentSubmissions: sortedChildContentSubmissions,
          }
        : undefined,
    [response.data, sortedChildContentSubmissions]
  );

  return {...response, data};
}

export default useContentSubmissionQuery;
